import React, { createContext, useContext } from 'react';
import { useAuthorization } from 'contexts/AuthContextProvider';
import { useSnackbar } from "./SnackbarContextProvider";

import { useTranslation } from "react-i18next";

const ServiceContext = createContext();

export const useService = () => useContext(ServiceContext);

export const ServiceProvider = ({ children }) => {
    const { accessToken, authAxios, getLoggedUser, logout } = useAuthorization();
    const { showSnackbar } = useSnackbar();

    const { t } = useTranslation();

    const getProjectWorkers = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/workers/?compress=true`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching project workers:", error);
            throw error;
        }
    };

    const getWorkspaceWorkers = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/workers/?compress=false`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching workspace workers:", error);
            throw error;
        }
    };

    const getWorkspaceWorkersForComments = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/workers/comments/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching workspace workers for comments:", error);
            throw error;
        }
    };

    const AddUserToProject = async (workspace_id, project_id, user_id) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/workers/add/`,
                data: { user_id: user_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error adding user to project:", error);
            throw error;
        }
    };

    const RemoveUserFromProject = async (workspace_id, project_id, user_id) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/workers/remove/`,
                data: { user_id: user_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error removing user from project:", error);
            throw error;
        }
    };

    const RemoveUserFromWorkspace = async (workspace_id, user_id) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/workers/remove/`,
                data: { user_id: user_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error removing user from workspace:", error);
            throw error;
        }
    };

    const getUserNotifications = async (user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}users/${user_id}/notifications/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Fetching Notifications failed:', error.message);
            throw error;
        }
    };

    const MarkNotificationAsRead = async (notification_id) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/notifications/${notification_id}/read/`,
                data: [],
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Mark Notification As Read failed:', error.message);
            throw error;
        }
    };

    const MarkNotificationAsUnread = async (notification_id) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/notifications/${notification_id}/unread/`,
                data: [],
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Mark Notification As Unread failed:', error.message);
            throw error;
        }
    };

    const SendWorkspaceInvite = async (workspace_id, email) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}users/workspace/${workspace_id}/invite/${email}/`,
                data: [],
                headers: { "Authorization": `Bearer ${accessToken}` }
            });

            if (response && response.data) {
                if (response.status === 201) {
                    showSnackbar(t("modal.user_invite"), 'success');
                    return response.data;
                }

            }
        } catch (error) {
            console.error('Sending Workspace Invite failed:', error.message);
            if (error.response) {
                const { status, data } = error.response;

                if (status === 400) {
                    showSnackbar(data.detail, 'error');
                    return;
                }

            }
            showSnackbar(error.message, 'error');
        }
    };

    const WorkspaceInviteUpdate = async (notification_id, invite_identifier, invite_answer, user_id) => {
        try {
            const base_response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}users/notifications/${notification_id}/request/`,
                data: { AcceptedInvite: invite_answer },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });

            if (invite_answer && base_response.data.option_select !== "Workspace-Invite-Expired") {
                await authAxios({
                    method: 'put',
                    url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${invite_identifier}/add-user/`,
                    data: { AcceptedInvite: invite_answer, user_id: user_id },
                    headers: { "Authorization": `Bearer ${accessToken}` }
                });
            }
            return base_response;
        } catch (error) {
            if (error.response.data.code === 'max_members') {
                showSnackbar(t("service.workspace_max_members"), 'error');
            } else if (error.response.status === 404) {
                showSnackbar(t("service.workspace_not_exist"), 'error');
            }
            throw error;
        }
    };



    const promoteWorkspaceWorkers = async (workspace_id, user_id) => {
        try {
            await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/workers/promote/`,
                data: { user_id: user_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
        } catch (error) {
            console.error('Failed to promote user:', error.message);
            throw error;
        }
    };

    const demoteWorkspaceWorkers = async (workspace_id, user_id) => {
        try {
            await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/workers/demote/`,
                data: { user_id: user_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
        } catch (error) {
            console.error('Failed to demote user:', error.message);
            throw error;
        }
    };



    const CreateNewList = async (workspace_id, project_id, list_name, board_id) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/new/`,
                data: { title: list_name, board: board_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to create new list:', error.message);
            throw error;
        }
    };

    const CreateNewTask = async (workspace_id, project_id, task_name, list_id, selectedLabels, pasted_cover) => {
        try {
            const user_id = getLoggedUser();
            const formData = new FormData();

            formData.append('title', task_name);
            formData.append('list', list_id);
            formData.append('user_id', user_id);
            selectedLabels.forEach(labelId => {
                formData.append('selectedLabels', labelId);
            });

            if (pasted_cover) {
                formData.append('cover_image', pasted_cover);
            }

            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/tasks/new/`,
                data: formData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "multipart/form-data"
                }
            });
            return response;
        } catch (error) {
            console.error('Failed to create new task:', error.message);
            throw error;
        }
    };

    const GetBoardDetails = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/details/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Failed to fetch board details:', error.message);
            throw error;
        }
    };


    const GetProjectBoardInformation = async (project_id, workspace_id, params = {}) => {
        try {

            const hash = window.location.hash;
            if (hash && hash.startsWith('#')) {
                const frontendOrder = hash.substring(1).split('-')[0];
                params.task = frontendOrder;
            }
            // Construct query string from params
            const queryParams = new URLSearchParams({
                ...params,
                compress: true
            }).toString();
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/?${queryParams}`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to fetch project board information:', error.message);
            throw error;
        }
    };

    const MarkAllNotificationAsRead = async () => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/notifications/mark-all-read/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to fetch tasks for list:', error.message);
            throw error;
        }
    };

    const GetTasksForList = async (workspace_id, project_id, listId, params = {}) => {

        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${listId}/tasks/?${queryParams}`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to fetch tasks for list:', error.message);
            throw error;
        }
    };


    const UpdateList = async (workspace_id, project_id, selectedList, list_new_title, board_id) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${selectedList.id}/update/`,
                data: { title: list_new_title, board: board_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to update list:', error.message);
            throw error;
        }
    };

    const UpdateListColor = async (workspace_id, project_id, list_id, color) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/update/color/`,
                data: { id: list_id, color: color },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to update list color:', error.message);
            throw error;
        }
    };

    const UpdateListCollapsable = async (workspace_id, project_id, list_id) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/update/collapsable/`,
                data: { id: list_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to update list collapsibility:', error.message);
            throw error;
        }
    };



    const ArchiveList = async (workspace_id, project_id, selectedList) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${selectedList.id}/delete/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            if (error.response && error.response.status === 403) {
                return { error: error.response.data.detail };
            } else {
                throw error;
            }
        }
    };

    const DeleteArchivedTasksInList = async (workspace_id, project_id, list_id) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/archives/delete_tasks`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            if (error.response && error.response.status === 403) {
                return { error: error.response.data.detail };
            } else {
                throw error;
            }
        }
    };

    const toggleTaskCount = async (workspace_id, project_id, list_id, new_bool) => {
        try {
            await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/task_count/`,
                data: { show_total_tasks: new_bool },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
        } catch (error) {
            console.error('Error toggling task count:', error.message);
        }
    };

    const UpdateTask = async (workspace_id, project_id, selectedList, selectedTask, task_new_title) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${selectedList.id}/tasks/${selectedTask.id}/update/`,
                data: {
                    title: task_new_title,
                    description: selectedTask.description,
                    list: selectedList.id,
                    task_id: selectedTask.id,
                    activity_log: "has updated the title of this task."
                },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error updating task:', error.message);
            throw error;
        }
    };

    const ArchiveTask = async (workspace_id, project_id, selectedList, selectedTask) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${selectedList.id}/tasks/${selectedTask.id}/delete/`,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "multipart/form-data"
                }
            });
            showSnackbar(t('archives.archive_task'), 'success');
            return response;
        } catch (error) {
            console.error('Error archiving task:', error.message);
            showSnackbar(t('archives.archive_task_fail'), 'error');
            throw error;
        }
    };

    const UpdateImportantInformation = async (workspace_id, project_id, board_id, important_info) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/important_info/update/`,
                data: { important_information: important_info },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error updating important information:', error.message);
            throw error;
        }
    };

    const AddFileToStorage = async (workspace_id, project_id, board_id, formData, onUploadProgress) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/storage/new/`,
                data: formData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress  // Progress callback to track upload progress
            });
            return response;
        } catch (error) {
            if (error.response && error.response.data.code === 'max_storage') {
                showSnackbar(error.response.data.detail, 'error');
            }
            console.error('Error adding file:', error.message);
            throw error;
        }
    };



    const DeleteStorageFile = async (workspace_id, project_id, board_id, file_id) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/storage/${file_id}/delete/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error deleting file from storage:', error.message);
            throw error;
        }
    };

    const DeleteResource = async (workspace_id, project_id, board_id, resource_id) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/resource/${resource_id}/delete/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error deleting resource:', error.message);
            throw error;
        }
    };

    const AddAResource = async (workspace_id, project_id, board_id, new_title, new_hyperlink, new_visibility) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/resource/new/`,
                data: { title: new_title, hyperlink: new_hyperlink, visibility: new_visibility },
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Accept": "application/json"
                }
            });
            return response;
        } catch (error) {
            console.error('Error adding resource:', error.message);
            throw error;
        }
    };

    const AddASecret = async (workspace_id, project_id, board_id, new_title, new_secret, new_visibility) => {
        try {
            const utf8ToB64 = (str) => {
                const utf8Encoder = new TextEncoder();
                const utf8Bytes = utf8Encoder.encode(str);
                return btoa(String.fromCharCode(...utf8Bytes));
            };
            const base64Secret = utf8ToB64(new_secret);

            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/secret/new/`,
                data: {
                    title: new_title,
                    secret: base64Secret,
                    type_of_visibility: new_visibility.toLowerCase()
                },
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Accept": "application/json"
                }
            });
            return response;
        } catch (error) {
            console.error('Error adding secret:', error.message);
            throw error;
        }
    };


    const DeleteSecret = async (workspace_id, project_id, board_id, secret_id) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/secret/${secret_id}/delete/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error deleting secret:", error.message);
            throw error;
        }
    };

    const SaveTaskLabels = async (workspace_id, project_id, task_id, labels) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/labels/update/`,
                data: { label: labels, task_id: task_id, activity_log: "has updated the Labels on this task." },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error saving task labels:", error.message);
            throw error;
        }
    };

    const AddLabelToBoard = async (workspace_id, project_id, board_id, new_title, new_color, new_description) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/labels/new/`,
                data: { color: new_color, title: new_title, description: new_description },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error adding label to board:", error.message);
            throw error;
        }
    };

    const ToggleFullLabel = async (workspace_id, project_id, board_id) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/labels/full/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error toggling full labels:", error.message);
            throw error;
        }
    };

    const DeleteLabel = async (workspace_id, project_id, board_id, label_id) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/labels/${label_id}/delete/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error deleting label:", error.message);
            throw error;
        }
    };

    const updateTaskMembers = async (workspace_id, project_id, task_id, new_members, notify_user) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/members/update/`,
                data: { members: new_members, task_id: task_id, activity_log: "has changed members to this task.", notify_user: notify_user },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error updating members:", error.message);
            throw error;
        }
    };

    const getUserProfile = async (user_id, workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}users/${user_id}/profile/?workspace_id=${workspace_id}`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching user profile:", error.message);
            throw error;
        }
    };

    const getUserActivityLogs = async (workspace_id, user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/user/${user_id}/activitylogs/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching user activity logs:", error.message);
            throw error;
        }
    };

    const getUserBoardActivityLogs = async (workspace_id, project_id, user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/user/${user_id}/activitylogs/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching user activity logs:", error.message);
            throw error;
        }
    };


    const getUserTimeZone = async (user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}users/${user_id}/timezone/`,
                headers: {
                    "Authorization": "Bearer " + accessToken
                }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            //We should only enter here when he can't find the user... but the user is also logged in?
            //So there's something weird going on, so let's log him out to fix it!
            //As to clean/reset the tokens would be re-logging in.
            logout();
            console.error("Error getting profile:", error);
            throw error;
        }
    };

    const ResubscribePlan = async (workspace_id, subscriptionId) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}/workflow/workspaces/${workspace_id}/resubscribe/${subscriptionId}/`,
                headers: {
                    "Authorization": "Bearer " + accessToken
                }
            });

            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error resubscribing:", error);
            throw error;
        }
    };


    const WhoAmI = async () => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}users/whoami/`,
                headers: {
                    "Authorization": "Bearer " + accessToken
                }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                console.error("Unauthorized error (401):", error.response.data);
            }
            console.error("Error getting profile:", error);
            //throw error;
        }
    };

    const WhoAmISupportInfo = async () => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}users/whoamisupport/`,
                headers: {
                    "Authorization": "Bearer " + accessToken
                }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error getting profile:", error);
            //throw error;
        }
    };


    const updateUserPersonalInfo = async (user_id, personalInfo) => {
        const formData = new FormData();
        Object.keys(personalInfo).forEach(key => {
            formData.append(key, personalInfo[key]);
        });

        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/${user_id}/profile/update/personal/`,
                data: formData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response;
        } catch (error) {
            console.error("Error updating personal information:", error.message);
            throw error;
        }
    };

    const updateUserWorkflowInfo = async (user_id, workflowInfo) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/${user_id}/profile/update/workflow/`,
                data: workflowInfo,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            return response;
        } catch (error) {
            console.error("Error updating workflow information:", error.message);
            throw error;
        }
    };

    const getUserStatData = async (user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/profile/`,
                params: { user_id: user_id },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error getting profile:", error.message);
            throw error;
        }
    };



    const GetTaskInformation = async (workspace_id, project_id, task_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/details/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error getting Activity Log of Task:", error.message);
            throw error;
        }
    };

    const fetchMoreComments = async (workspace_id, project_id, task_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/comments/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response;
            }
        } catch (error) {
            console.error("Error loading more comments:", error.message);
            throw error;
        }
    };

    const fetchMoreReplies = async (workspace_id, project_id, task_id, comment_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/comments/${comment_id}/replies/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response;
            }
        } catch (error) {
            console.error("Error loading more replies:", error.message);
            throw error;
        }
    };

    const getMoreChecklistItems = async (workspace_id, project_id, checklist_id, offset, limit) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklist_id}/load/items/?offset=${offset}&limit=${limit}`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error loading more replies:", error.message);
            throw error;
        }
    };


    const GetFullActivityLogs = async (workspace_id, project_id, task_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/full-activity-logs/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error loading all activity logs:", error.message);
            throw error;
        }
    };



    const GetTaskActivityLog = async (workspace_id, project_id, task_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/activity_log/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error getting Activity Log of Task:", error.message);
            throw error;
        }
    };

    const UpdateExistingProject = async (formData, project_id, workspace_id) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/update/`,
                data: formData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response;
        } catch (error) {
            console.error("Error updating Project:", error.message);
            throw error;
        }
    };

    const getProjectInformation = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/`,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error retrieving project:", error.message);
            throw error;
        }
    };

    const ArchiveExistingProject = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/archive/`,
                data: [],
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error archiving Project:", error.message);
            throw error;
        }
    };

    const DeleteExistingProject = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/delete/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error deleting Project:", error.message);
            throw error;
        }
    };

    const deleteWorkspace = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/delete/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error deleting Workspace:", error.message);
            throw error;
        }
    };

    const UpdateTaskDescription = async (workspace_id, project_id, task_id, description) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/description/update/`,
                data: { description },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            if (error.response?.data?.code === 'max_storage') {
                showSnackbar(error.response.data.detail, 'error');
            }
            console.error("Error updating Project:", error.message);
            throw error;
        }
    };


    const updateListHeader = async (workspace_id, project_id, list_id, headerData) => {
        const formData = new FormData();
        formData.append('emoji', headerData.emoji);
        formData.append('title', headerData.title);
        formData.append('title_color', headerData.title_color);
        formData.append('color', headerData.color);
        if (headerData.image) {
            formData.append('image', headerData.image);
        }

        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/header/update/`,
                data: formData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response;
        } catch (error) {
            console.error("Error updating header:", error.message);
            throw error;
        }
    };

    const CreateChecklist = async (workspace_id, project_id, taskId, newtitle) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${taskId}/checklists/`,
                data: { title: newtitle, task: taskId },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error creating checklist:", error.message);
            throw error;
        }
    };

    const GetChecklists = async (workspace_id, project_id, taskId) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${taskId}/checklists/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching checklists:", error.message);
            throw error;
        }
    };

    const CreateChecklistItem = async (workspace_id, project_id, checklistId, title) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklistId}/items/`,
                data: { title: title, checklist: checklistId },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error creating checklist item:", error.message);
            throw error;
        }
    };

    const UpdateChecklistItem = async (workspace_id, project_id, checklistId, itemId, data) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklistId}/items/${itemId}/`,
                data,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error updating checklist item:", error.message);
            throw error;
        }
    };

    const DeleteChecklistItem = async (workspace_id, project_id, itemId) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/items/${itemId}/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error deleting checklist item:", error.message);
            throw error;
        }
    };

    const DeleteChecklist = async (workspace_id, project_id, checklistId) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklistId}/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error deleting checklist:", error.message);
            throw error;
        }
    };

    const UpdateChecklistTitle = async (workspace_id, project_id, checklistId, newTitle) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklistId}/update/`,
                data: { title: newTitle },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error updating checklist title:", error.message);
            throw error;
        }
    };

    const ToggleHiddenStatusOfChecklists = async (workspace_id, project_id, checklistId) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklistId}/update/hidden/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error updating checklist title:", error.message);
            throw error;
        }
    };

    const ToggleHiddenStatusOfChecklistItems = async (workspace_id, project_id, checklistId) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklistId}/update/hidden/items/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error updating checklist title:", error.message);
            throw error;
        }
    };




    const UpdateChecklistOrder = async (workspace_id, project_id, checklistId, order) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/checklists/${checklistId}/update/`,
                data: { order: order },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error updating checklist order:", error.message);
            throw error;
        }
    };

    const getProjectCustomOrder = async (workspaceId) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/projects/reorder/${workspaceId}/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching project custom order:", error.message);
            throw error;
        }
    };


    const reorderProjects = async (data, workspaceId) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/projects/reorder/${workspaceId}/`,
                data,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error reordering projects:", error.message);
            throw error;
        }
    };

    const getUserProjects = async (workspaceId) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspaceId}/projects/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Fetching Projects failed:', error.message);
            throw error;
        }
    };

    const updateWorkspaceData = async (workspace_id, formData) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/update/`,
                data: formData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response;
        } catch (error) {
            console.error('Error saving workspace:', error.message);
            throw error;
        }
    };

    const getProjectStatus = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/status/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error fetching project status:', error.message);
            throw error;
        }
    };

    const UpdateProjectStatus = async (project_id, data, workspace_id) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/projects/${project_id}/status/`,
                data,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error updating project status:', error.message);
            throw error;
        }
    };

    const updateUserPassword = async (passwordData) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}users/change-password/`,
                data: passwordData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            return response;
        } catch (error) {
            console.error("Error updating password:", error.message);
            throw error;
        }
    };

    const updateUserProfileImage = async (user_id, imageFile) => {
        const formData = new FormData();
        formData.append('image', imageFile);

        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/${user_id}/profile/update/image/`,
                data: formData,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error updating profile image:", error.message);
            throw error;
        }
    };

    const calculateBoardStats = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/calculate-stats/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error calculating board stats:", error.message);
        }
    };

    const GetProjectBoardArchives = async (workspace_id, project_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/archives/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Error fetching board archives:", error.message);
            throw error;
        }
    };

    const UnarchiveTask = async (workspace_id, project_id, selected_task_id, selectedListId, retainPosition, newPosition) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${selected_task_id}/unarchive/`,
                data: {
                    list: selectedListId,
                    retainPosition,
                    newPosition: retainPosition ? null : newPosition
                },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });

            return response;
        } catch (error) {
            console.error("Failed to unarchive task:", error.message);
            throw error;
        }
    };


    const UnarchiveList = async (workspace_id, project_id, list_id) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/unarchive/`,
                data: {},
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Failed to unarchive list:", error.response?.data || error.message);
            return { error: error.response?.data || error.message };
        }
    };

    const UpdateLabelTitle = async (label_id, newTitle) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/labels/${label_id}/`,
                data: { title: newTitle },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Failed to update label", error.message);
        }
    };

    const UpdateLabel = async (workspace_id, project_id, label_id, newTitle, newColor, newDescription) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/labels/${label_id}/update/`,
                data: { title: newTitle, color: newColor, description: newDescription },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Failed to update label", error.message);
        }
    };



    const UpdateTaskDragging = async (workspace_id, project_id, taskId, sourceListId, destinationListId, newPosition) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${taskId}/move/`,
                data: {
                    source_list_id: sourceListId,
                    destination_list_id: destinationListId,
                    new_position: newPosition,
                },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            console.error("Failed to update task during dragging:", error.message);
            return null;
        }
    };


    const UpdateBoardListsDragging = async (workspace_id, project_id, lists, board_id) => {
        //Sorry if the name is unintuitive, this is dragging lists!
        try {
            await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/${board_id}/lists/drag/`,
                data: { lists: lists.map(({ id, order }) => ({ id, order })) },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
        } catch (error) {
            console.error("Failed to update board lists during dragging:", error.message);
        }
    };

    const UpdateTaskStatus = async (taskId, status, targetIndex = null, members) => {
        try {
            await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/tasks/${taskId}/status/`,
                data: { status, targetIndex, members },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
        } catch (error) {
            console.error("Failed to update task status:", error.message);
        }
    };

    const UpdateTaskDates = async (workspace_id, project_id, task_id, convertedDates) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/dates/update/`,
                data: convertedDates,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error("Failed to update task dates:", error.message);
        }
    };

    const fetchWorkspaceCalendar = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/?compress=true`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            if (error.response.status === 403 || error.response.status === 404) {
                window.location.href = '/workflow/feature-unavailable';
                showSnackbar(t("service.calendar_no_permission"), 'error');
            }
            console.error('Error fetching events:', error.message);
            throw error;
        }
    };

    const fetchWorkspaceCalendarWithVacations = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/?with_vacations=true`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            if (error.response.status === 403) {
                window.location.href = '/workflow/feature-unavailable';
                showSnackbar(t("service.calendar_no_permission"), 'error');
            }
            console.error('Error fetching events:', error.message);
            throw error;
        }
    };

    const fetchWorkspaceVacations = async (workspaceId) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspaceId}/vacations/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching vacation data:", error.message);
            throw error;
        }
    };

    const submitNewVacation = async (vacationData, workspaceId) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspaceId}/vacations/new/`,
                data: vacationData,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error submitting vacation request:', error.message);
            throw error;
        }
    };

    const GetPendingVacationRequests = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/vacations/pending/`,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            console.error('Error fetching pending vacation requests:', error.message);
        }
    };

    const UpdatePendingVacationRequest = async (workspace_id, vacationData, action, changeReason = '', updatedFields = null) => {
        try {
            const payload = {
                vacation_ids: vacationData,
                action: action,
                reason: changeReason,
            };

            if (updatedFields) {
                payload.updated_fields = updatedFields;
            }

            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/vacations/pending/`,
                data: payload,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error updating pending vacation request:', error.message);
        }
    };

    const DeleteVacationRequest = async (workspace_id, vacationData) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/vacations/delete/`,
                data: { vacation_ids: vacationData },
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error deleting vacation request:', error.message);
        }
    };

    const getMyRemainingWorkspaceVacationDays = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/vacations/remaining/`,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error fetching remaining vacation days:', error.message);
        }
    };

    const getUserRemainingWorkspaceVacationDays = async (workspace_id, user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/vacations/remaining/${user_id}/`,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error fetching user remaining vacation days:', error.message);
        }
    };

    const getWorkingHours = async (workspace_id, user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/profile/working_hours/${user_id}/`,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response.data[0]; // Assuming the first user is always returned
        } catch (error) {
            console.error('Error fetching working hours:', error.message);
        }
    };

    const updateUserWorkflowWorkingHours = async (workspace_id, working_hours) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/profile/working_hours/update/`,
                data: { working_hours },
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error updating working hours:', error.response || error.message);
        }
    };

    const UpdateTaskCover = async (workspace_id, project_id, task_id, formData) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/update_cover/`,
                data: formData,
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response;
        } catch (error) {
            console.error('Error updating task cover:', error.response ? error.response.data : error.message);
            throw error;
        }
    };

    const getWorkspaceEvents = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/events/list/`,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching workspace events:', error.message);
            throw error;
        }
    };

    const createWorkspaceEvent = async (eventData, workspace_id) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/events/new/`,
                data: eventData,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error creating workspace event:', error.message);
            throw error;
        }
    };

    const deleteWorkspaceEvent = async (eventId, workspaceId) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspaceId}/events/delete/`,
                data: { event_id: eventId },
                headers: { Authorization: `Bearer ${accessToken}` }
            });

            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error creating workspace event:', error.message);
            throw error;
        }
    };


    const fetchWorkspaceHolidays = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/holidays/`,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching holidays:', error.message);
            throw error;
        }
    };

    const updateHolidayBlacklist = async (workspaceId, payload) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspaceId}/holidays/blacklist/`,
                data: payload,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error updating holiday blacklist:", error);
            throw error;
        }
    };

    const deleteHoliday = async (workspaceId, holidayId) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspaceId}/holidays/${holidayId}/delete/`,
                headers: {
                    "Authorization": `Bearer ${accessToken}`, // Ensure accessToken is defined
                    "Content-Type": "application/json",
                },
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error deleting holiday:", error);
            throw error;
        }
    };



    const createHoliday = async (workspace_id, holidayData) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/holidays/new/`,
                data: holidayData,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error creating holiday:', error.message);
            throw error;
        }
    };

    const updateWorkspaceEvent = async (eventData, workspace_id, event_id) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/calendar/events/${workspace_id}/events/update/${event_id}/`,
                data: eventData,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error updating event:', error.message);
            throw error;
        }
    };

    const purchasePlan = async (workspace_id, plan, extras, discount_code) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/purchase/`,
                data: { plan, extras, discount_code },
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error purchasing plan:', error.message);
            throw error;
        }
    };

    const fetchPlans = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/plans/${workspace_id}/`,
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching subscription plans:', error.message);
        }
    };

    const fetchExtras = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/extras/${workspace_id}/`,
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching subscription extras:', error.message);
        }
    };

    const HandleUploadAttachment = async (workspace_id, project_id, task_id, formData, config = {}) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/upload_attachment/`,
                data: formData,
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'multipart/form-data'
                },
                ...config
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error uploading attachments:', error.response ? error.response.data : error.message);
            throw error;
        }
    };



    const DeleteTaskAttachment = async (workspace_id, project_id, task_id, attachment_id, deleteFromStorage) => {
        try {
            const response = await authAxios({
                method: 'delete',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/attachments/${attachment_id}/delete/`,
                params: { delete_from_storage: deleteFromStorage },
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Error deleting attachment:', error.response ? error.response.data : error.message);
            throw error;
        }
    };


    const PostTaskComment = async (workspace_id, project_id, task_id, commentData) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/tasks/${task_id}/comments/new/`,
                data: commentData,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error posting comment:', error.message);
            throw error;
        }
    };

    const fetchSubscriptions = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/purchase/`,
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error.message);
        }
    };

    const cancelSubscription = async (workspace_id, subscription_id) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/purchase/cancel`,
                data: { subscription_id },
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error.message);
        }
    };

    const suspendSubscription = async (workspace_id, subscription_id) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/purchase/suspend`,
                data: { subscription_id },
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error('Error suspending subscription:', error.message);
        }
    };

    const SearchQueryingWorkspaceDatabase = async (workspace_id, project_id, search_query) => {
        const encodedQuery = encodeURIComponent(search_query);
        if (project_id) {
            try {
                const response = await authAxios({
                    method: 'get',
                    url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/search_project/${project_id}/?query=${encodedQuery}`,
                    headers: { Authorization: `Bearer ${accessToken}` }
                });
                if (response && response.data) {
                    return response.data;
                }
            } catch (error) {
                console.error('Error searching workspace database:', error.message);
            }
        } else {
            try {
                const response = await authAxios({
                    method: 'get',
                    url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspace_id}/search/?query=${encodedQuery}`,
                    headers: { Authorization: `Bearer ${accessToken}` }
                });
                if (response && response.data) {
                    return response.data;
                }
            } catch (error) {
                console.error('Error searching workspace database:', error.message);
            }
        }
    };

    const GetServerStatus = async () => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}settings/maintenance/`
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching server status:", error.message);
            throw error;
        }
    };

    const CurrentUserOngoingTickets = async () => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/tickets/ongoing/`
            });

            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching server status:", error.message);
            throw error;
        }
    };

    const GetTicketChat = async (ticket_id) => {

        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/tickets/${ticket_id}/chats/`
            });
            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching chat messages:", error.message);
            throw error;
        }
    };

    const SendTicketMessage = async (ticket_id, formData) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/tickets/${ticket_id}/chats/new`,
                data: formData,
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                }
            });
            return response;
        } catch (error) {
            console.error("Error sending message:", error);
            throw error;
        }
    };




    const CurrentUserPastTickets = async () => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/tickets/past/`
            });

            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching server status:", error.message);
            throw error;
        }
    };



    const CreateNewTicket = async (ticketData) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/tickets/create/`,
                data: ticketData,
            });

            return response.data;  // Return the created ticket data
        } catch (error) {
            console.error('Error creating new ticket:', error);
            throw error;  // Handle error in the calling function
        }
    };

    const ReopenTicket = async (ticket_id) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/tickets/${ticket_id}/reopen/`,
            });

            return response.data;
        } catch (error) {
            console.error("Error reopening ticket:", error);
            throw error;
        }
    };

    const GetUserLastAccessedProjects = async (user_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/last_accessed/${user_id}/`,
            });

            return response.data;
        } catch (error) {
            console.error("Error reopening ticket:", error);
            throw error;
        }
    };

    const GetDiscount = async (code) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/discount/${code}/`,
            });

            return response.data;
        } catch (error) {
            console.error("Error getting discount:", error);
            throw error;
        }
    };

    const updateWorkspaceJob = async (workspace_id, new_job) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/${workspace_id}/update/job/`,
                data: {
                    job: new_job
                }
            });

            return response.data;
        } catch (error) {
            console.error("Error updating workspace job:", error);
            throw error;
        }
    };

    const UpdateResource = async (workspace_id, project_id, board_id, resource_id, resourceData) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/resource/${resource_id}/update/`,
                headers: { "Authorization": `Bearer ${accessToken}` },
                data: resourceData
            });
            return response;
        } catch (error) {
            console.error('Error updating resource:', error);
            throw error;
        }
    };

    const UpdateSecret = async (workspace_id, project_id, board_id, secret_id, secretData) => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/board/${board_id}/secret/${secret_id}/update/`,
                headers: { "Authorization": `Bearer ${accessToken}` },
                data: secretData  // Send the updated secret data
            });
            return response;
        } catch (error) {
            console.error('Error updating secret:', error.message);
            throw error;
        }
    };



    const getWorkspacePublicKey = async (workspaceId) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspace/${workspaceId}/public_key/`,
                headers: { "Authorization": `Bearer ${accessToken}` },
            });
            return response.data.public_key;
        } catch (error) {
            console.error("Error fetching public key:", error);
        }
    };

    const downloadEncryptedFile = async (workspace_id, attachment_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/storage/download/${attachment_id}/`,
                headers: { "Authorization": `Bearer ${accessToken}` },
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            console.error("Error fetching public key:", error);
        }
    };




    const workspaceInvitationAccept = async (invite_id) => {
        try {
            const worspace_response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspace-invite/${invite_id}/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });

            await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}users/notifications/${invite_id}/accept/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });

            return worspace_response.data
        } catch (error) {
            console.error('Failed to join workspace:', error.message);
            throw error;
        }
    };

    const workspaceInvitationInfo = async (invite_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspace-invite/${invite_id}/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response.data;

        } catch (error) {
            if (error.response.status === 404) {
                showSnackbar(t("service.invite_not_available"), 'error');
                return false
            }
            console.error("Failed to get workspace invitation info:", error);
            throw error;
        }
    };

    const CurrentUTCTime = async () => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/UTC/current/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response.data.utc_datetime;  // Returns the UTC time as a string
        } catch (error) {
            console.error("Failed to fetch current UTC time:", error);
            throw error;
        }
    };

    const getUserSettings = async (workspace_id) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}users/user-settings/${workspace_id}/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user settings:', error);
            throw error;
        }
    };

    const updateUserSetting = async (settingKey, settingValue, workspace_id) => {
        try {
            await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/user-settings/${workspace_id}/`,
                data: { [settingKey]: settingValue },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
        } catch (error) {
            console.error(`Failed to update setting ${settingKey}:`, error);
            throw error;
        }
    };

    const getStaffUserSettings = async () => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}users/staff-user-settings/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user settings:', error);
            throw error;
        }
    };

    const updateStaffUserSetting = async (setting_key, setting_value) => {
        try {
            await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}users/staff-user-settings/`,
                data: { [setting_key]: setting_value },
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
        } catch (error) {
            console.error(`Failed to update setting ${setting_key}:`, error);
            throw error;
        }
    };

    const sendConfirmationEmail = async (workspaceId, newOwnerId) => {
        try {
            const response = await authAxios({
                method: 'post',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspaceId}/transfer_ownership/`,
                data: { new_owner_id: newOwnerId },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            return response.data;
        } catch (error) {
            console.error('Error sending confirmation email:', error.message);
            throw error;
        }
    };

    const verifyConfirmationCode = async (workspaceId, confirmationCode) => {
        try {
            const response = await authAxios({
                method: 'patch',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspaces/${workspaceId}/transfer_ownership/`,
                data: { confirmation_code: confirmationCode },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            return response.data;
        } catch (error) {
            console.error('Error verifying confirmation code:', error.message);
            throw error;
        }
    };

    const getFilteredAndSortedList = async (workspace_id, project_id, list_id, paramsData) => {
        try {
            const params = new URLSearchParams(paramsData).toString();

            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${list_id}/filters/?${params}`,
                headers: {
                    "Authorization": `Bearer ${accessToken}`
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching filtered and sorted list:", error.message);
            throw error;
        }
    };


    const getTasksFilters = async (workspace_id, project_id, listId, params = {}) => {

        try {
            const queryParams = new URLSearchParams(params).toString();
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${listId}/taskfilters/?${queryParams}`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
            
        } catch (error) {
            console.error('Failed to fetch tasks for list:', error.message);
            throw error;
        }
    };


    const GetTasksFilteredForList = async (workspace_id, project_id, listId, params = {}) => {
        try {
            // Serialize the taskFilters object to a JSON string
            if (params.taskFilters) {
                params.taskFilters = JSON.stringify(params.taskFilters);
            }

            const queryParams = new URLSearchParams(params).toString();
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/lists/${listId}/tasks/filters/?${queryParams}`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response;
        } catch (error) {
            console.error('Failed to fetch tasks for list:', error.message);
            throw error;
        }
    };

    const getUpdateNews = async (params = {}) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/news/updates/`,
                headers: { "Authorization": `Bearer ${accessToken}` },
                params,
            });
            if (response && response.data) {
                return {
                    results: response.data.results,
                    next: response.data.next,
                    previous: response.data.previous,
                    last_seen_version: response.data.last_seen_version,
                };
            }
        } catch (error) {
            console.error("Error fetching updates:", error);
            throw error;
        }
    };

    const markUpdatesAsSeen = async () => {
        try {
            const response = await authAxios({
                method: 'put',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/news/updates/mark-seen/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            return response.data;
        } catch (error) {
            console.error("Error marking updates as seen:", error);
            throw error;
        }
    };

    const WorkspaceStats = async (workspaceId, params = {}) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}workflow/workspace-statistics/${workspaceId}/list-stats/`,
                params: params
            });

            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching workspace stats:", error.message);
            throw error;
        }
    };

    const WorkspaceStatsTaskView = async (workspaceId, params = {}) => {
        try {
            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}/workflow/workspace-statistics/${workspaceId}/list-stats/task-statistics/`,
                params: params,
            });


            if (response && response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching workspace stats task view:", error.message);
            throw error;
        }
    };


    const CheckTaskExistenceInBoard = async (workspace_id, project_id, board_id, taskHash) => {
        try {
            // Remove the "#" character if present
            let cleanedTaskHash = taskHash.startsWith('#') ? taskHash.slice(1) : taskHash;
            cleanedTaskHash = cleanedTaskHash.split('-')[0]

            const response = await authAxios({
                method: 'get',
                url: `${process.env.REACT_APP_BACKEND_URL}boards/workspaces/${workspace_id}/projects/${project_id}/boards/${board_id}/${cleanedTaskHash}/exists/`,
                headers: { "Authorization": `Bearer ${accessToken}` }
            });

            if (response && response.data) {
                return response.data.exists;
            }
            return false;
        } catch (error) {
            console.error('Failed to check task existence:', error.message);
            throw error;
        }
    };


    return (
        <ServiceContext.Provider value={{ CheckTaskExistenceInBoard, WorkspaceStatsTaskView, WorkspaceStats, markUpdatesAsSeen, GetTasksFilteredForList, getTasksFilters, getFilteredAndSortedList, getUpdateNews, sendConfirmationEmail, verifyConfirmationCode, deleteWorkspaceEvent, deleteHoliday, updateHolidayBlacklist, MarkAllNotificationAsRead, getUserBoardActivityLogs, ResubscribePlan, getStaffUserSettings, updateStaffUserSetting, getProjectInformation, getUserSettings, updateUserSetting, GetFullActivityLogs, getMoreChecklistItems, fetchMoreReplies, fetchMoreComments, GetTaskInformation, GetTasksForList, CurrentUTCTime, workspaceInvitationInfo, workspaceInvitationAccept, downloadEncryptedFile, getWorkspacePublicKey, GetDiscount, UpdateSecret, UpdateResource, ToggleHiddenStatusOfChecklistItems, ToggleHiddenStatusOfChecklists, updateWorkspaceJob, GetUserLastAccessedProjects, ReopenTicket, CreateNewTicket, CurrentUserPastTickets, SendTicketMessage, WhoAmISupportInfo, GetTicketChat, CurrentUserOngoingTickets, GetServerStatus, DeleteArchivedTasksInList, SearchQueryingWorkspaceDatabase, fetchWorkspaceCalendarWithVacations, getUserActivityLogs, suspendSubscription, cancelSubscription, fetchSubscriptions, PostTaskComment, DeleteTaskAttachment, HandleUploadAttachment, UpdateTaskCover, RemoveUserFromWorkspace, UpdateLabel, purchasePlan, fetchPlans, fetchExtras, updateWorkspaceEvent, createHoliday, fetchWorkspaceHolidays, getWorkspaceEvents, createWorkspaceEvent, getUserRemainingWorkspaceVacationDays, updateUserWorkflowWorkingHours, getWorkingHours, getMyRemainingWorkspaceVacationDays, DeleteVacationRequest, GetPendingVacationRequests, UpdatePendingVacationRequest, submitNewVacation, fetchWorkspaceVacations, fetchWorkspaceCalendar, UpdateTaskDates, UpdateTaskStatus, UpdateBoardListsDragging, UpdateTaskDragging, UpdateLabelTitle, DeleteExistingProject, UnarchiveList, UnarchiveTask, GetProjectBoardArchives, calculateBoardStats, updateUserProfileImage, updateUserPersonalInfo, updateUserWorkflowInfo, updateUserPassword, UpdateProjectStatus, getProjectStatus, updateWorkspaceData, UpdateChecklistOrder, getUserProjects, getProjectCustomOrder, reorderProjects, UpdateListCollapsable, UpdateListColor, DeleteChecklist, UpdateChecklistTitle, CreateChecklist, GetChecklists, CreateChecklistItem, UpdateChecklistItem, DeleteChecklistItem, updateListHeader, UpdateTaskDescription, deleteWorkspace, ArchiveExistingProject, UpdateExistingProject, GetTaskActivityLog, WhoAmI, getUserTimeZone, getUserStatData, getUserProfile, updateTaskMembers, DeleteLabel, ToggleFullLabel, SaveTaskLabels, AddLabelToBoard, DeleteResource, DeleteStorageFile, AddFileToStorage, AddAResource, AddASecret, DeleteSecret, getUserNotifications, MarkNotificationAsRead, MarkNotificationAsUnread, SendWorkspaceInvite, WorkspaceInviteUpdate, getWorkspaceWorkersForComments, getWorkspaceWorkers, getProjectWorkers, AddUserToProject, RemoveUserFromProject, CreateNewList, CreateNewTask, GetProjectBoardInformation, UpdateList, ArchiveList, UpdateTask, ArchiveTask, toggleTaskCount, promoteWorkspaceWorkers, demoteWorkspaceWorkers, GetBoardDetails, UpdateImportantInformation }}>
            {children}
        </ServiceContext.Provider>
    );
};
